<template>
    <DCard
        :title="__('customers.business_incidents.connected_contracts.title', {numberOfItems: items.length})"
        :main-title-has-slot="true"
        padding
    >
        <template #titleHasSlot>
            <DSwitch
                color="success"
                variant="3d"
                :disabled="isInLoadingState"
                :checked="showCardData"
                @update:checked="showCardSection"
            />
        </template>
        <DRow v-if="showCardData">
            <DCol col="6">
                <p>
                    <small class="text-muted">{{ __('customers.business_incidents.connected_contracts.hint') }}</small>
                </p>
                <section class="contracts-table">
                    <div class="table-header">
                        <div>{{ __('customers.business_incidents.connected_contracts.contract_id') }}</div>
                        <div>{{ __('customers.business_incidents.connected_contracts.product') }}</div>
                        <div>
                            <DButton
                                v-if="userPermissions.includes('customer.incidents_manage') && incident.status === 'open'"
                                id="add"
                                icon="plus"
                                mini
                                :disabled="isInLoadingState"
                                @click="addContracts"
                            />
                        </div>
                    </div>
                    <main>
                        <div
                            v-for="(item, index) in items"
                            :key="index"
                            class="item-group"
                        >
                            <div class="item-header">
                                <div>
                                    <Link :href="contractRoute(item)">
                                        {{ item.contract_id }}
                                    </Link>
                                </div>
                                <div>
                                    {{ item.product }}
                                </div>
                                <div>
                                    <DButton
                                        v-if="userPermissions.includes('customer.incidents_manage') && incident.status === 'open'"
                                        :id="`delete_contract_${item.id}`"
                                        :disabled="isInLoadingState"
                                        icon="trash-can-outline"
                                        color="danger"
                                        mini
                                        @click="showRemoveItem(item)"
                                    />
                                </div>
                            </div>
                        </div>
                    </main>
                </section>
            </DCol>
        </DRow>
        <AddItems
            v-if="showAddContractModal"
            :show-modal="showAddContractModal"
            :incident="incident"
            :title="__('customers.business_incidents.connected_contracts.modal.add.title')"
            :label="__('customers.business_incidents.connected_contracts.modal.add.label')"
            :section="'contracts'"
            @close-modal="closeAddModal"
            @confirm-modal="save"
        />
        <RemoveItem
            v-if="showRemoveItemModal"
            :show-modal="showRemoveItemModal"
            :title="__('customers.business_incidents.connected_contracts.modal.remove.title')"
            :message="__('customers.business_incidents.connected_contracts.modal.remove.message', {contract: contractId})"
            @close-modal="closeRemoveModal"
            @confirm-modal="remove"
        />
    </DCard>
</template>

<script setup>
import { route } from '~@/Composables/route'
import { computed, ref } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import RemoveItem from '~@/Components/Customers/BusinessIncidents/RemoveItem'
import AddItems from '~@/Components/Customers/BusinessIncidents/AddItems'

const props = defineProps({
    items: {
        type: Array,
        default () {
            return []
        }
    },
    incident: {
        type: Object,
        default () {
            return {}
        }
    },
    showCard: {
        type: Boolean,
        default () {
            return true
        }
    }
})

const emit = defineEmits(['addContracts'])

const showCardData = ref(props.showCard)
const showRemoveItemModal = ref(false)
const showAddContractModal = ref(false)
const contractId = ref(null)
const businessIncidentContractId = ref(null)
const userPermissions = computed(() => usePage().props.auth.permissions)

function showCardSection (value) {
    showCardData.value = value
}

function contractRoute (item) {
    return route('customers.customers.contracts.show', { customer: props.incident.customer_id, contract: item.contract_id })
}

function showRemoveItem (item) {
    businessIncidentContractId.value = item.id
    contractId.value = item.contract_id
    showRemoveItemModal.value = true
}

function addContracts () {
    showAddContractModal.value = true
}

function closeRemoveModal () {
    businessIncidentContractId.value = null
    contractId.value = null
    showRemoveItemModal.value = false
}

function remove () {
    router.delete(route('customers.customers.delete-business-incident-contract', { businessIncidentContract: businessIncidentContractId.value }))
    showRemoveItemModal.value = false
}

function closeAddModal () {
    showAddContractModal.value = false
}

function save (form) {
    emit('addContracts', form)

    showAddContractModal.value = false
}

</script>

<style lang="scss" scoped>
.contracts-table {
    & .table-header {
        border-bottom: 1px solid rgba(0,0,0,0.14);
        display: grid;
        grid-template-columns: 30% 50% 20%;
        grid-gap: 10px;
        align-items: center;

        & > div {
            padding: 10px;
            font-weight: bold;
        }
    }
    & main {
        & .item-group {
            border-bottom: 1px solid rgba(0,0,0,0.3);
            position: relative;

            & .item-header {
                display: grid;
                grid-template-columns: 30% 50% 20%;
                grid-gap: 10px;
                background: #fff;
                border-bottom: 1px solid rgba(0,0,0,0.14);
                position: sticky;
                top: -25px;
                width: 100%;
                z-index: 100;

                & > div {
                    padding: 10px;
                    align-self: center;
                }
            }
        }
    }
}
</style>
